import type { OpenRisk } from '@apna/shared'
import { cn } from '@/lib/utils'
import { compactInr } from './quantity'

/**
 * What a position has AT STAKE, on one line — ROADMAP 6.12, 6.13.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE NUMBER, AND IT HOLDS STILL
 *
 * The first version of this answered "what could this lose today": a worst
 * case inside a one-session band, priced with Black-76 against live implied
 * volatilities. Every input moved, so the line read "-1.94 L if ±2,194 pts"
 * and both halves changed on every frame.
 *
 * Reported 2026-11-01: that is the wrong question. The one this line is for
 * is "how much is at stake", and the answer must not move unless the
 * position does. At expiry an option is worth its intrinsic value, so the
 * figure comes from strikes, signed quantities and prices already paid —
 * frozen data, no spot, no volatility, no clock.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT STILL NEVER SAYS "UNLIMITED" ALONE
 *
 *     OPEN RISK  -₹20.7 K  at expiry     UNLIMITED  -₹65/pt above 20,150
 *                └ the worst at any      └ and past there it keeps going,
 *                  spot, for a capped      at this speed, with nothing
 *                  structure a constant    stopping it
 *
 * Both halves are now constants. The rate is a slope and the level is a
 * strike, so a naked short reads the same from one glance to the next — and
 * a speed is something a human can multiply by the gap they are afraid of,
 * which is the decision they were trying to make.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WORDS, AND WHY EACH ONE SURVIVED A REVISION
 *
 *   "at 2%"     became "if ±400 pts", then "at expiry". A percentage is a
 *               multiplication the reader performs against a spot they have
 *               to remember; a band was points but moved; the horizon does
 *               not move at all.
 *   "▲"         became words. A glyph that needs a legend is not a label,
 *               and this one carries the most important fact on the row.
 *   "UNCAPPED"  became "UNLIMITED", the phrase every option seller in this
 *               market already uses. The reason to avoid it was never the
 *               word — it was the word ALONE, with no number beside it.
 *
 * A short PUT is not the same claim: spot cannot go below zero, so its loss
 * is large but FINITE and is reported as a figure rather than a tail.
 * Telling a trader their put is unlimited teaches them to ignore the word on
 * the call.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function OpenRiskChip({
  risk,
  /** `lg` for the drawer's header line, `sm` for a basket card. */
  size = 'sm',
  testId,
}: {
  risk: OpenRisk
  size?: 'sm' | 'lg'
  testId?: string
}) {
  if (risk.kind === 'FLAT') return null

  const big = size === 'lg'

  if (risk.kind === 'UNKNOWN') {
    /*
     * NEVER a zero and never blank. A basket that cannot be priced is the one
     * most worth looking at, and an empty space reads as "nothing at stake".
     */
    return (
      <span
        className={cn('inline-flex items-center gap-1 font-semibold text-warning', big ? 'text-[11px]' : 'text-[9.5px]')}
        title={`Open risk cannot be measured: ${risk.why}`}
        data-testid={testId}
      >
        RISK NOT KNOWN
      </span>
    )
  }

  /*
   * A position whose worst case inside the band is a PROFIT still has risk
   * worth naming when its tail is open — that is precisely the short that
   * looks safe right up until it is not. The number shown is the worst case
   * either way; only the colour changes.
   */
  const losing = risk.worst < 0

  return (
    <span
      className={cn(
        /*
         * SHRINKABLE ON A CARD — 9.34.
         *
         * This was `shrink-0` everywhere. Inside the card header's
         * `flex-1 min-w-0` wrapper that meant the chip kept its full
         * intrinsic width and simply OVERFLOWED, painting across the mode
         * pill, the account and the P&L beside it. The header had already
         * given up `flex-wrap` on purpose, so there was nowhere for the
         * excess to go and it overlapped instead.
         *
         * The drawer headline keeps `shrink-0`: it is the one place the
         * figure is the subject rather than an annotation.
         */
        'inline-flex min-w-0 items-center gap-1.5',
        big ? 'shrink-0 text-[11px]' : 'shrink text-[9.5px]',
      )}
      data-testid={testId}
    >
      <span
        className={cn(
          // `nowrap`: "OPEN RISK" broke across two lines and pushed the whole
          // row out of shape before anything had even overflowed.
          'whitespace-nowrap font-bold tracking-[0.12em] text-muted-foreground',
          big && 'text-[10px]',
        )}
      >
        OPEN RISK
      </span>
      <span
        className={cn(
          'num whitespace-nowrap font-bold',
          big && 'text-[14px] leading-none',
          losing ? 'text-destructive' : 'text-muted-foreground',
        )}
        title={
          risk.conservative === true
            ? `AT MOST this, at any spot — the worst point is ${risk.atSpot.toLocaleString('en-IN')}. ` +
              'Legs expiring later than the nearest expiry were valued at intrinsic, so they gave up ' +
              'time value they still hold: the real worst case is better than this and cannot be worse. ' +
              'No volatility was assumed.'
            : `The worst this can do at expiry, at any spot — it happens at ` +
              `${risk.atSpot.toLocaleString('en-IN')}. It changes only when the position does, ` +
              'never with spot or volatility. This is what is at stake, not what is blocked as margin.'
        }
      >
        {/*
          ── 9.28. "AT MOST", AS A CHARACTER ──────────────────────────────
          A calendar read at its nearest expiry is a BOUND, not the figure.
          Printing it bare would claim a precision the inputs do not have;
          printing nothing at all was the old behaviour and said RISK NOT
          KNOWN about a position whose loss is provably capped.
        */}
        {risk.conservative === true && <span className="opacity-70">≤ </span>}
        {compactInr(risk.worst)}
      </span>
      {/*
        "AT EXPIRY" IS THE QUALIFIER THAT REPLACED THE BAND.
        The old line read "if ±2,194 pts (1-day move)" and both halves of it
        moved on every tick. Two words that never change say what the number
        is, and on a card even they are dropped — it is the same two words
        fifteen times down a drawer, and the tooltip carries them.
      */}
      {big && <span className="tr-risk-band whitespace-nowrap text-muted-foreground">at expiry</span>}
      {risk.beyond && (
        /*
         * The open tail, in words. A capped spread and a naked short can
         * read identically inside the band and are not the same position.
         */
        <span
          className={cn(
            /*
             * OUTLINED, NOT FILLED — and the difference is the whole lesson
             * of the version before this one.
             *
             * A solid red block is right for something rare. This appears on
             * EVERY short, which on a book of straddles is every card: the
             * drawer became fifteen red banners and the P&L beside them
             * disappeared. A warning that is always on is wallpaper, and it
             * takes the attention it was meant to earn away from the numbers
             * the trader is actually reading.
             *
             * A thin red outline is still unmistakably a warning, still the
             * only red-bordered thing on the row, and leaves the P&L and the
             * risk figure as the loudest numbers — which is correct, because
             * they are the ones that change.
             */
            'inline-flex items-center gap-1 whitespace-nowrap rounded border font-bold uppercase tracking-wide',
            'border-destructive/40 bg-destructive/5 text-destructive',
            big ? 'px-1.5 py-[2px] text-[9px]' : 'px-1 py-[1px] text-[8px]',
          )}
          data-testid={testId ? `${testId}-tail` : undefined}
          title={
            `Past ${risk.beyond.fromSpot.toLocaleString('en-IN')} this keeps losing about ` +
            `₹${Math.round(risk.beyond.perPoint).toLocaleString('en-IN')} for every further point ` +
            `${risk.beyond.side === 'ABOVE' ? 'UP' : 'DOWN'}. ` +
            (risk.beyond.bounded
              ? 'It does end — spot cannot fall below zero — but not anywhere you would want to find out.'
              : 'Nothing caps it. A stop is the only thing that will.')
          }
        >
          {risk.beyond.bounded ? 'TILL ZERO' : 'UNLIMITED'}
          {/*
            THE RATE IS HEADLINE-ONLY — 9.34. On a card it was the widest
            part of the widest chip and the first thing to collide with the
            P&L. The word is the warning; the rate is detail, and the
            tooltip above carries it in full on every size.
          */}
          {big && (
            <span className="num font-bold">
              -₹{Math.round(risk.beyond.perPoint).toLocaleString('en-IN')}/pt
            </span>
          )}
          {/*
            The level is on the header line only. On a card it is the
            basket's own strike, which is already printed in its name and in
            the leg below — three copies of 20,150 on one card.
          */}
          {big && (
            <span className="tr-risk-level font-medium opacity-80">
              {risk.beyond.side === 'ABOVE' ? 'above' : 'below'}{' '}
              <span className="num">{risk.beyond.fromSpot.toLocaleString('en-IN')}</span>
            </span>
          )}
        </span>
      )}
    </span>
  )
}
